import React from 'react';
import { useLocation } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import ConfirmationModal from '../modal/ConfirmationModal';
import useUser from '../../hooks/global/useUser';
import { LoadedUser } from '../../interfaces';
import { ROUTES } from '../../constants/global';
import useGetSubscription from '../../hooks/subscriptions/useGetSubscription';
import useFreeTrialStatus from '../../hooks/subscriptions/useFreeTrialStatus';
import useSubscriptionModal from '../../hooks/subscriptions/useSubscriptionModal';
import {
  canUserManageSubscription,
  getSubscriptionModalContent,
} from '../../helpers/subscriptions';
import { createTerminalPortalSession } from '../../services/subscriptions';

/**
 * Modal when **terminal** subscription payment has failed (recurring terminal/reader plan).
 * Pops up on every page load/refresh when payment has failed. Does not show when the
 * (Porter) subscription alert modal is visible or when on the subscription page.
 * "Update payment" opens the Stripe customer portal to manage payment method.
 */
function TerminalSubscriptionPaymentFailedModal() {
  const location = useLocation();
  const { user } = useUser<LoadedUser>();
  const [dismissed, setDismissed] = React.useState(false);

  const { data: subscriptionData } = useGetSubscription();
  const { daysRemaining, inFreeTrial } = useFreeTrialStatus();
  const { dismissed: subscriptionModalDismissed } = useSubscriptionModal();

  const subscriptionModalContent = getSubscriptionModalContent({
    daysRemaining,
    inFreeTrial: !!inFreeTrial,
    subscriptionData,
    canManageSubscription: canUserManageSubscription(user),
  });
  const isSubscriptionModalVisible =
    !!subscriptionModalContent && !subscriptionModalDismissed;

  const terminalPaymentFailed =
    !!user?.business?.terminalStripeSubscriptionId &&
    !!user?.business?.terminalSubscriptionPaymentFailed;
  const isOnSubscriptionPage = location.pathname === ROUTES.subscription;

  const open =
    terminalPaymentFailed &&
    !dismissed &&
    !isSubscriptionModalVisible &&
    !isOnSubscriptionPage;

  const handleClose = React.useCallback(() => {
    setDismissed(true);
  }, []);

  const portalMutation = useMutation({
    mutationFn: () => {
      const businessId = user?.business?.id;
      if (!businessId) throw new Error('No business');
      return createTerminalPortalSession(businessId);
    },
    onSuccess: data => {
      const url = data?.url;
      if (url) window.location.href = url;
    },
    onError: () => {
      toast.error('Failed to open payment portal.');
    },
  });

  const { mutate: launchTerminalPortal } = portalMutation;

  const handleUpdatePayment = React.useCallback(() => {
    launchTerminalPortal();
  }, [launchTerminalPortal]);

  return (
    <ConfirmationModal
      open={open}
      handleClose={handleClose}
      title="Terminal subscription payment failed"
      message="The payment for your terminal subscription failed. Please update your payment information to avoid interruption."
      positiveAction={handleUpdatePayment}
      positiveText="Update payment"
      negativeAction={handleClose}
      negativeText="Dismiss"
      actionIsLoading={portalMutation.isPending || portalMutation.isSuccess}
    />
  );
}

export default TerminalSubscriptionPaymentFailedModal;
